/**
 * 值的序列化與限制檢查。對應 docs/protocol.md §12。
 *
 * JSON-only 是三個下游需求共同決定的：
 * 要能過 postMessage、要能寫進時間軸、要能餵給 LLM。
 */

import { MAX_PAYLOAD_BYTES } from './constants.ts'
import { busError } from './errors.ts'
import type { BusError } from './types.ts'

export type EncodeResult =
  | { ok: true; bytes: number; clone: unknown }
  | { ok: false; error: BusError }

const encoder = new TextEncoder()

/**
 * 檢查值是否 JSON 可序列化且不超過大小上限，並回傳一份獨立副本。
 * 回傳副本是刻意的：發布之後呼叫者再改動原物件，不得影響 bus 上的值。
 */
export function encodeValue(value: unknown, path: string): EncodeResult {
  let json: string | undefined
  try {
    json = JSON.stringify(value)
  } catch (cause) {
    return {
      ok: false,
      error: busError('E_SCHEMA', '值無法序列化為 JSON（可能含循環參照）', {
        path,
        hint: '值必須是 JSON 可序列化的：物件、陣列、字串、數字、布林、null。',
        details: String(cause),
      }),
    }
  }

  if (json === undefined) {
    return {
      ok: false,
      error: busError('E_SCHEMA', '值不得為 undefined、函式或 symbol', {
        path,
        hint: '要表達「沒有值」請用 null。',
      }),
    }
  }

  const bytes = encoder.encode(json).length
  if (bytes > MAX_PAYLOAD_BYTES) {
    return {
      ok: false,
      error: busError('E_TOO_LARGE', `酬載 ${bytes} 位元組，超過上限 ${MAX_PAYLOAD_BYTES}`, {
        path,
        hint: '大型資料請改走 handle 或 URL，bus 只傳位址。',
      }),
    }
  }

  return { ok: true, bytes, clone: JSON.parse(json) as unknown }
}
